/*
|--------------------------------------------------------------------------
| Footer Shader - Interactive Fluid/Splash Cursor Effect
|
| A WebGL fluid simulation that responds to cursor movement
| Creates organic, flowing splashes in Alchemy's violet brand color
|--------------------------------------------------------------------------
*/

// Configuration
const config = {
  SIM_RESOLUTION: 100,
  DYE_RESOLUTION: 600,
  SPLAT_RADIUS: 0.12,
  SPLAT_FORCE: 3000,
  CURL: 30,
  PRESSURE_ITERATIONS: 20,
  VELOCITY_DISSIPATION: 0.985,
  DENSITY_DISSIPATION: 0.98,
  // Alchemy violet color (normalized RGBA)
  COLOR: { r: 0.714, g: 0.306, b: 1.0, a: 0.5 },
}

// Shader sources
const baseVertexShader = `
  precision highp float;
  attribute vec2 aPosition;
  varying vec2 vUv;
  varying vec2 vL;
  varying vec2 vR;
  varying vec2 vT;
  varying vec2 vB;
  uniform vec2 texelSize;

  void main () {
    vUv = aPosition * 0.5 + 0.5;
    vL = vUv - vec2(texelSize.x, 0.0);
    vR = vUv + vec2(texelSize.x, 0.0);
    vT = vUv + vec2(0.0, texelSize.y);
    vB = vUv - vec2(0.0, texelSize.y);
    gl_Position = vec4(aPosition, 0.0, 1.0);
  }
`

const clearShader = `
  precision mediump float;
  precision mediump sampler2D;
  varying highp vec2 vUv;
  uniform sampler2D uTexture;
  uniform float value;

  void main () {
    gl_FragColor = value * texture2D(uTexture, vUv);
  }
`

const displayShader = `
  precision highp float;
  precision highp sampler2D;
  varying vec2 vUv;
  uniform sampler2D uTexture;

  void main () {
    vec3 c = texture2D(uTexture, vUv).rgb;
    float intensity = max(c.r, max(c.g, c.b));
    vec3 muted = c * 0.15;
    float a = intensity * 0.05;
    gl_FragColor = vec4(muted, a);
  }
`

const splatShader = `
  precision highp float;
  precision highp sampler2D;
  varying vec2 vUv;
  uniform sampler2D uTarget;
  uniform float aspectRatio;
  uniform vec4 color;
  uniform vec2 point;
  uniform float radius;

  void main () {
    vec2 p = vUv - point.xy;
    p.x *= aspectRatio;
    float d = exp(-dot(p, p) / radius);
    vec3 splat = d * color.rgb * color.a;
    vec3 base = texture2D(uTarget, vUv).xyz;
    gl_FragColor = vec4(base + splat, 1.0);
  }
`

const advectionShader = `
  precision highp float;
  precision highp sampler2D;
  varying vec2 vUv;
  uniform sampler2D uVelocity;
  uniform sampler2D uSource;
  uniform vec2 texelSize;
  uniform float dt;
  uniform float dissipation;

  void main () {
    vec2 coord = vUv - dt * texture2D(uVelocity, vUv).xy * texelSize;
    gl_FragColor = dissipation * texture2D(uSource, coord);
    gl_FragColor.a = 1.0;
  }
`

const divergenceShader = `
  precision mediump float;
  precision mediump sampler2D;
  varying highp vec2 vUv;
  varying highp vec2 vL;
  varying highp vec2 vR;
  varying highp vec2 vT;
  varying highp vec2 vB;
  uniform sampler2D uVelocity;

  void main () {
    float L = texture2D(uVelocity, vL).x;
    float R = texture2D(uVelocity, vR).x;
    float T = texture2D(uVelocity, vT).y;
    float B = texture2D(uVelocity, vB).y;
    vec2 C = texture2D(uVelocity, vUv).xy;
    if (vL.x < 0.0) { L = -C.x; }
    if (vR.x > 1.0) { R = -C.x; }
    if (vT.y > 1.0) { T = -C.y; }
    if (vB.y < 0.0) { B = -C.y; }
    float div = 0.5 * (R - L + T - B);
    gl_FragColor = vec4(div, 0.0, 0.0, 1.0);
  }
`

const curlShader = `
  precision mediump float;
  precision mediump sampler2D;
  varying highp vec2 vUv;
  varying highp vec2 vL;
  varying highp vec2 vR;
  varying highp vec2 vT;
  varying highp vec2 vB;
  uniform sampler2D uVelocity;

  void main () {
    float L = texture2D(uVelocity, vL).y;
    float R = texture2D(uVelocity, vR).y;
    float T = texture2D(uVelocity, vT).x;
    float B = texture2D(uVelocity, vB).x;
    float vorticity = R - L - T + B;
    gl_FragColor = vec4(0.5 * vorticity, 0.0, 0.0, 1.0);
  }
`

const vorticityShader = `
  precision highp float;
  precision highp sampler2D;
  varying vec2 vUv;
  varying vec2 vL;
  varying vec2 vR;
  varying vec2 vT;
  varying vec2 vB;
  uniform sampler2D uVelocity;
  uniform sampler2D uCurl;
  uniform float curl;
  uniform float dt;

  void main () {
    float L = texture2D(uCurl, vL).x;
    float R = texture2D(uCurl, vR).x;
    float T = texture2D(uCurl, vT).x;
    float B = texture2D(uCurl, vB).x;
    float C = texture2D(uCurl, vUv).x;
    vec2 force = 0.5 * vec2(abs(T) - abs(B), abs(R) - abs(L));
    force /= length(force) + 0.0001;
    force *= curl * C;
    force.y *= -1.0;
    vec2 vel = texture2D(uVelocity, vUv).xy;
    gl_FragColor = vec4(vel + force * dt, 0.0, 1.0);
  }
`

const pressureShader = `
  precision mediump float;
  precision mediump sampler2D;
  varying highp vec2 vUv;
  varying highp vec2 vL;
  varying highp vec2 vR;
  varying highp vec2 vT;
  varying highp vec2 vB;
  uniform sampler2D uPressure;
  uniform sampler2D uDivergence;

  void main () {
    float L = texture2D(uPressure, vL).x;
    float R = texture2D(uPressure, vR).x;
    float T = texture2D(uPressure, vT).x;
    float B = texture2D(uPressure, vB).x;
    float C = texture2D(uPressure, vUv).x;
    float divergence = texture2D(uDivergence, vUv).x;
    float pressure = (L + R + B + T - divergence) * 0.25;
    gl_FragColor = vec4(pressure, 0.0, 0.0, 1.0);
  }
`

const gradientSubtractShader = `
  precision mediump float;
  precision mediump sampler2D;
  varying highp vec2 vUv;
  varying highp vec2 vL;
  varying highp vec2 vR;
  varying highp vec2 vT;
  varying highp vec2 vB;
  uniform sampler2D uPressure;
  uniform sampler2D uVelocity;

  void main () {
    float L = texture2D(uPressure, vL).x;
    float R = texture2D(uPressure, vR).x;
    float T = texture2D(uPressure, vT).x;
    float B = texture2D(uPressure, vB).x;
    vec2 velocity = texture2D(uVelocity, vUv).xy;
    velocity.xy -= vec2(R - L, T - B);
    gl_FragColor = vec4(velocity, 0.0, 1.0);
  }
`

interface Pointer {
  id: number
  texcoordX: number
  texcoordY: number
  prevTexcoordX: number
  prevTexcoordY: number
  deltaX: number
  deltaY: number
  down: boolean
  moved: boolean
}

interface FBO {
  texture: WebGLTexture
  fbo: WebGLFramebuffer
  width: number
  height: number
  texelSizeX: number
  texelSizeY: number
  attach: (id: number) => number
}

interface DoubleFBO {
  width: number
  height: number
  texelSizeX: number
  texelSizeY: number
  read: FBO
  write: FBO
  swap: () => void
}

interface Program {
  program: WebGLProgram
  uniforms: { [key: string]: WebGLUniformLocation | null }
  bind: () => void
}

class FluidSimulation {
  private canvas: HTMLCanvasElement
  private gl: WebGLRenderingContext
  private ext: {
    formatRGBA: { internalFormat: number; format: number }
    formatRG: { internalFormat: number; format: number }
    formatR: { internalFormat: number; format: number }
    halfFloatTexType: number
    supportLinearFiltering: boolean
  }

  private pointers: Pointer[] = []
  private splatStack: number[] = []

  private programs: {
    clear: Program
    display: Program
    splat: Program
    advection: Program
    divergence: Program
    curl: Program
    vorticity: Program
    pressure: Program
    gradientSubtract: Program
  }

  private dye!: DoubleFBO
  private velocity!: DoubleFBO
  private divergence!: FBO
  private curl!: FBO
  private pressure!: DoubleFBO

  private blit: (target: FBO | null, clear?: boolean) => void

  private animationId: number = 0
  private lastUpdateTime: number = 0
  private isVisible: boolean = false
  private resizeObserver: ResizeObserver | null = null
  private intersectionObserver: IntersectionObserver | null = null
  private container: HTMLElement

  constructor(container: HTMLElement) {
    this.container = container

    // Create canvas
    this.canvas = document.createElement('canvas')
    this.canvas.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 0;
    `
    container.style.position = 'relative'
    container.insertBefore(this.canvas, container.firstChild)

    // Get WebGL context
    const params = {
      alpha: true,
      depth: false,
      stencil: false,
      antialias: false,
      preserveDrawingBuffer: false,
    }

    this.gl = (this.canvas.getContext('webgl', params) ||
      this.canvas.getContext(
        'experimental-webgl',
        params,
      )) as WebGLRenderingContext

    if (!this.gl) {
      console.warn('[FluidSimulation] WebGL not supported')
      return
    }

    // Get extensions
    this.ext = this.getWebGLExtensions()

    // Compile shaders
    this.programs = {
      clear: this.createProgram(baseVertexShader, clearShader),
      display: this.createProgram(baseVertexShader, displayShader),
      splat: this.createProgram(baseVertexShader, splatShader),
      advection: this.createProgram(baseVertexShader, advectionShader),
      divergence: this.createProgram(baseVertexShader, divergenceShader),
      curl: this.createProgram(baseVertexShader, curlShader),
      vorticity: this.createProgram(baseVertexShader, vorticityShader),
      pressure: this.createProgram(baseVertexShader, pressureShader),
      gradientSubtract: this.createProgram(
        baseVertexShader,
        gradientSubtractShader,
      ),
    }

    // Create blit function
    this.blit = this.createBlitFunction()

    // Initialize framebuffers
    this.initFramebuffers()

    // Initialize pointer
    this.pointers.push(this.createPointer())

    // Setup event listeners
    this.setupEventListeners()

    // Setup observers
    this.resizeObserver = new ResizeObserver(() => this.resize())
    this.resizeObserver.observe(container)

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        this.isVisible = entries[0].isIntersecting
        if (this.isVisible && !this.animationId) {
          this.lastUpdateTime = Date.now()
          this.update()
        }
      },
      { threshold: 0 },
    )
    this.intersectionObserver.observe(container)

    // Initial setup
    this.resize()
    this.lastUpdateTime = Date.now()

    // Start animation
    this.update()

    console.log('[FluidSimulation] Initialized successfully')
  }

  private getWebGLExtensions() {
    const gl = this.gl

    gl.getExtension('OES_texture_half_float')
    gl.getExtension('OES_texture_half_float_linear')

    const halfFloatTexType =
      gl.getExtension('OES_texture_half_float')?.HALF_FLOAT_OES ||
      gl.UNSIGNED_BYTE

    let formatRGBA = { internalFormat: gl.RGBA, format: gl.RGBA }
    let formatRG = { internalFormat: gl.RGBA, format: gl.RGBA }
    let formatR = { internalFormat: gl.RGBA, format: gl.RGBA }

    const supportLinearFiltering = !!gl.getExtension(
      'OES_texture_half_float_linear',
    )

    return {
      formatRGBA,
      formatRG,
      formatR,
      halfFloatTexType,
      supportLinearFiltering,
    }
  }

  private createProgram(vertexSource: string, fragmentSource: string): Program {
    const gl = this.gl

    const vertexShader = gl.createShader(gl.VERTEX_SHADER)!
    gl.shaderSource(vertexShader, vertexSource)
    gl.compileShader(vertexShader)

    const fragmentShader = gl.createShader(gl.FRAGMENT_SHADER)!
    gl.shaderSource(fragmentShader, fragmentSource)
    gl.compileShader(fragmentShader)

    const program = gl.createProgram()!
    gl.attachShader(program, vertexShader)
    gl.attachShader(program, fragmentShader)
    gl.linkProgram(program)

    const uniforms: { [key: string]: WebGLUniformLocation | null } = {}
    const uniformCount = gl.getProgramParameter(program, gl.ACTIVE_UNIFORMS)
    for (let i = 0; i < uniformCount; i++) {
      const uniformName = gl.getActiveUniform(program, i)!.name
      uniforms[uniformName] = gl.getUniformLocation(program, uniformName)
    }

    return {
      program,
      uniforms,
      bind: () => gl.useProgram(program),
    }
  }

  private createBlitFunction() {
    const gl = this.gl

    const buffer = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer)
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, -1, 1, 1, 1, 1, -1]),
      gl.STATIC_DRAW,
    )

    const indexBuffer = gl.createBuffer()
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, indexBuffer)
    gl.bufferData(
      gl.ELEMENT_ARRAY_BUFFER,
      new Uint16Array([0, 1, 2, 0, 2, 3]),
      gl.STATIC_DRAW,
    )

    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0)
    gl.enableVertexAttribArray(0)

    return (target: FBO | null, clear = false) => {
      if (target === null) {
        gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight)
        gl.bindFramebuffer(gl.FRAMEBUFFER, null)
      } else {
        gl.viewport(0, 0, target.width, target.height)
        gl.bindFramebuffer(gl.FRAMEBUFFER, target.fbo)
      }
      if (clear) {
        gl.clearColor(0, 0, 0, 0)
        gl.clear(gl.COLOR_BUFFER_BIT)
      }
      gl.drawElements(gl.TRIANGLES, 6, gl.UNSIGNED_SHORT, 0)
    }
  }

  private createFBO(
    w: number,
    h: number,
    internalFormat: number,
    format: number,
    type: number,
    filter: number,
  ): FBO {
    const gl = this.gl

    gl.activeTexture(gl.TEXTURE0)
    const texture = gl.createTexture()!
    gl.bindTexture(gl.TEXTURE_2D, texture)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
    gl.texImage2D(gl.TEXTURE_2D, 0, internalFormat, w, h, 0, format, type, null)

    const fbo = gl.createFramebuffer()!
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo)
    gl.framebufferTexture2D(
      gl.FRAMEBUFFER,
      gl.COLOR_ATTACHMENT0,
      gl.TEXTURE_2D,
      texture,
      0,
    )
    gl.viewport(0, 0, w, h)
    gl.clear(gl.COLOR_BUFFER_BIT)

    const texelSizeX = 1.0 / w
    const texelSizeY = 1.0 / h

    return {
      texture,
      fbo,
      width: w,
      height: h,
      texelSizeX,
      texelSizeY,
      attach: (id: number) => {
        gl.activeTexture(gl.TEXTURE0 + id)
        gl.bindTexture(gl.TEXTURE_2D, texture)
        return id
      },
    }
  }

  private createDoubleFBO(
    w: number,
    h: number,
    internalFormat: number,
    format: number,
    type: number,
    filter: number,
  ): DoubleFBO {
    let fbo1 = this.createFBO(w, h, internalFormat, format, type, filter)
    let fbo2 = this.createFBO(w, h, internalFormat, format, type, filter)

    return {
      width: w,
      height: h,
      texelSizeX: fbo1.texelSizeX,
      texelSizeY: fbo1.texelSizeY,
      get read() {
        return fbo1
      },
      set read(value) {
        fbo1 = value
      },
      get write() {
        return fbo2
      },
      set write(value) {
        fbo2 = value
      },
      swap() {
        const temp = fbo1
        fbo1 = fbo2
        fbo2 = temp
      },
    }
  }

  private initFramebuffers() {
    const gl = this.gl

    const simRes = this.getResolution(config.SIM_RESOLUTION)
    const dyeRes = this.getResolution(config.DYE_RESOLUTION)

    const texType = this.ext.halfFloatTexType
    const rgba = this.ext.formatRGBA
    const filtering = this.ext.supportLinearFiltering ? gl.LINEAR : gl.NEAREST

    this.dye = this.createDoubleFBO(
      dyeRes.width,
      dyeRes.height,
      rgba.internalFormat,
      rgba.format,
      texType,
      filtering,
    )
    this.velocity = this.createDoubleFBO(
      simRes.width,
      simRes.height,
      rgba.internalFormat,
      rgba.format,
      texType,
      filtering,
    )
    this.divergence = this.createFBO(
      simRes.width,
      simRes.height,
      rgba.internalFormat,
      rgba.format,
      texType,
      gl.NEAREST,
    )
    this.curl = this.createFBO(
      simRes.width,
      simRes.height,
      rgba.internalFormat,
      rgba.format,
      texType,
      gl.NEAREST,
    )
    this.pressure = this.createDoubleFBO(
      simRes.width,
      simRes.height,
      rgba.internalFormat,
      rgba.format,
      texType,
      gl.NEAREST,
    )
  }

  private getResolution(resolution: number) {
    let aspectRatio = this.gl.drawingBufferWidth / this.gl.drawingBufferHeight
    if (aspectRatio < 1) aspectRatio = 1.0 / aspectRatio

    const min = Math.round(resolution)
    const max = Math.round(resolution * aspectRatio)

    if (this.gl.drawingBufferWidth > this.gl.drawingBufferHeight) {
      return { width: max, height: min }
    } else {
      return { width: min, height: max }
    }
  }

  private createPointer(): Pointer {
    return {
      id: -1,
      texcoordX: 0,
      texcoordY: 0,
      prevTexcoordX: 0,
      prevTexcoordY: 0,
      deltaX: 0,
      deltaY: 0,
      down: false,
      moved: false,
    }
  }

  private setupEventListeners() {
    // Use the container for pointer events, but canvas shows the effect
    this.container.style.pointerEvents = 'auto'

    this.container.addEventListener(
      'mousemove',
      this.handleMouseMove.bind(this),
    )
    this.container.addEventListener(
      'mouseenter',
      this.handleMouseEnter.bind(this),
    )
    this.container.addEventListener(
      'mouseleave',
      this.handleMouseLeave.bind(this),
    )
    this.container.addEventListener(
      'touchmove',
      this.handleTouchMove.bind(this),
      { passive: true },
    )
    this.container.addEventListener(
      'touchstart',
      this.handleTouchStart.bind(this),
      { passive: true },
    )
    this.container.addEventListener('touchend', this.handleTouchEnd.bind(this))
  }

  private handleMouseMove(e: MouseEvent) {
    const rect = this.canvas.getBoundingClientRect()
    const pointer = this.pointers[0]

    pointer.prevTexcoordX = pointer.texcoordX
    pointer.prevTexcoordY = pointer.texcoordY
    pointer.texcoordX = (e.clientX - rect.left) / rect.width
    pointer.texcoordY = 1.0 - (e.clientY - rect.top) / rect.height
    pointer.deltaX = pointer.texcoordX - pointer.prevTexcoordX
    pointer.deltaY = pointer.texcoordY - pointer.prevTexcoordY
    pointer.moved = Math.abs(pointer.deltaX) > 0 || Math.abs(pointer.deltaY) > 0
  }

  private handleMouseEnter(e: MouseEvent) {
    const rect = this.canvas.getBoundingClientRect()
    const pointer = this.pointers[0]

    pointer.down = true
    pointer.texcoordX = (e.clientX - rect.left) / rect.width
    pointer.texcoordY = 1.0 - (e.clientY - rect.top) / rect.height
    pointer.prevTexcoordX = pointer.texcoordX
    pointer.prevTexcoordY = pointer.texcoordY
  }

  private handleMouseLeave() {
    this.pointers[0].down = false
  }

  private handleTouchStart(e: TouchEvent) {
    const touches = e.targetTouches
    const rect = this.canvas.getBoundingClientRect()

    for (let i = 0; i < touches.length; i++) {
      if (i >= this.pointers.length) this.pointers.push(this.createPointer())

      const pointer = this.pointers[i]
      pointer.id = touches[i].identifier
      pointer.down = true
      pointer.texcoordX = (touches[i].clientX - rect.left) / rect.width
      pointer.texcoordY = 1.0 - (touches[i].clientY - rect.top) / rect.height
      pointer.prevTexcoordX = pointer.texcoordX
      pointer.prevTexcoordY = pointer.texcoordY
    }
  }

  private handleTouchMove(e: TouchEvent) {
    const touches = e.targetTouches
    const rect = this.canvas.getBoundingClientRect()

    for (let i = 0; i < touches.length; i++) {
      const pointer = this.pointers.find((p) => p.id === touches[i].identifier)
      if (!pointer) continue

      pointer.prevTexcoordX = pointer.texcoordX
      pointer.prevTexcoordY = pointer.texcoordY
      pointer.texcoordX = (touches[i].clientX - rect.left) / rect.width
      pointer.texcoordY = 1.0 - (touches[i].clientY - rect.top) / rect.height
      pointer.deltaX = pointer.texcoordX - pointer.prevTexcoordX
      pointer.deltaY = pointer.texcoordY - pointer.prevTexcoordY
      pointer.moved =
        Math.abs(pointer.deltaX) > 0 || Math.abs(pointer.deltaY) > 0
    }
  }

  private handleTouchEnd(e: TouchEvent) {
    const touches = e.changedTouches
    for (let i = 0; i < touches.length; i++) {
      const pointer = this.pointers.find((p) => p.id === touches[i].identifier)
      if (pointer) pointer.down = false
    }
  }

  private resize() {
    const dpr = Math.min(window.devicePixelRatio, 2)
    const width = Math.floor(this.canvas.clientWidth * dpr)
    const height = Math.floor(this.canvas.clientHeight * dpr)

    if (this.canvas.width !== width || this.canvas.height !== height) {
      this.canvas.width = width
      this.canvas.height = height
      this.initFramebuffers()
    }
  }

  private update = () => {
    if (!this.isVisible) {
      this.animationId = 0
      return
    }

    const dt = this.calcDeltaTime()
    this.step(dt)
    this.render()
    this.animationId = requestAnimationFrame(this.update)
  }

  private calcDeltaTime(): number {
    const now = Date.now()
    let dt = (now - this.lastUpdateTime) / 1000
    dt = Math.min(dt, 0.016666)
    this.lastUpdateTime = now
    return dt
  }

  private step(dt: number) {
    const gl = this.gl

    gl.disable(gl.BLEND)

    // Handle splats from pointer movement
    this.pointers.forEach((pointer) => {
      if (pointer.moved) {
        pointer.moved = false
        const dx = pointer.deltaX * config.SPLAT_FORCE
        const dy = pointer.deltaY * config.SPLAT_FORCE
        this.splat(pointer.texcoordX, pointer.texcoordY, dx, dy, config.COLOR)
      }
    })

    // Curl
    this.programs.curl.bind()
    gl.uniform2f(
      this.programs.curl.uniforms['texelSize'],
      this.velocity.texelSizeX,
      this.velocity.texelSizeY,
    )
    gl.uniform1i(
      this.programs.curl.uniforms['uVelocity'],
      this.velocity.read.attach(0),
    )
    this.blit(this.curl)

    // Vorticity
    this.programs.vorticity.bind()
    gl.uniform2f(
      this.programs.vorticity.uniforms['texelSize'],
      this.velocity.texelSizeX,
      this.velocity.texelSizeY,
    )
    gl.uniform1i(
      this.programs.vorticity.uniforms['uVelocity'],
      this.velocity.read.attach(0),
    )
    gl.uniform1i(this.programs.vorticity.uniforms['uCurl'], this.curl.attach(1))
    gl.uniform1f(this.programs.vorticity.uniforms['curl'], config.CURL)
    gl.uniform1f(this.programs.vorticity.uniforms['dt'], dt)
    this.blit(this.velocity.write)
    this.velocity.swap()

    // Divergence
    this.programs.divergence.bind()
    gl.uniform2f(
      this.programs.divergence.uniforms['texelSize'],
      this.velocity.texelSizeX,
      this.velocity.texelSizeY,
    )
    gl.uniform1i(
      this.programs.divergence.uniforms['uVelocity'],
      this.velocity.read.attach(0),
    )
    this.blit(this.divergence)

    // Clear pressure
    this.programs.clear.bind()
    gl.uniform1i(
      this.programs.clear.uniforms['uTexture'],
      this.pressure.read.attach(0),
    )
    gl.uniform1f(this.programs.clear.uniforms['value'], 0.8)
    this.blit(this.pressure.write)
    this.pressure.swap()

    // Pressure iterations
    this.programs.pressure.bind()
    gl.uniform2f(
      this.programs.pressure.uniforms['texelSize'],
      this.velocity.texelSizeX,
      this.velocity.texelSizeY,
    )
    gl.uniform1i(
      this.programs.pressure.uniforms['uDivergence'],
      this.divergence.attach(0),
    )
    for (let i = 0; i < config.PRESSURE_ITERATIONS; i++) {
      gl.uniform1i(
        this.programs.pressure.uniforms['uPressure'],
        this.pressure.read.attach(1),
      )
      this.blit(this.pressure.write)
      this.pressure.swap()
    }

    // Gradient subtract
    this.programs.gradientSubtract.bind()
    gl.uniform2f(
      this.programs.gradientSubtract.uniforms['texelSize'],
      this.velocity.texelSizeX,
      this.velocity.texelSizeY,
    )
    gl.uniform1i(
      this.programs.gradientSubtract.uniforms['uPressure'],
      this.pressure.read.attach(0),
    )
    gl.uniform1i(
      this.programs.gradientSubtract.uniforms['uVelocity'],
      this.velocity.read.attach(1),
    )
    this.blit(this.velocity.write)
    this.velocity.swap()

    // Advect velocity
    this.programs.advection.bind()
    gl.uniform2f(
      this.programs.advection.uniforms['texelSize'],
      this.velocity.texelSizeX,
      this.velocity.texelSizeY,
    )
    gl.uniform1i(
      this.programs.advection.uniforms['uVelocity'],
      this.velocity.read.attach(0),
    )
    gl.uniform1i(
      this.programs.advection.uniforms['uSource'],
      this.velocity.read.attach(0),
    )
    gl.uniform1f(this.programs.advection.uniforms['dt'], dt)
    gl.uniform1f(
      this.programs.advection.uniforms['dissipation'],
      config.VELOCITY_DISSIPATION,
    )
    this.blit(this.velocity.write)
    this.velocity.swap()

    // Advect dye
    gl.uniform1i(
      this.programs.advection.uniforms['uVelocity'],
      this.velocity.read.attach(0),
    )
    gl.uniform1i(
      this.programs.advection.uniforms['uSource'],
      this.dye.read.attach(1),
    )
    gl.uniform1f(
      this.programs.advection.uniforms['dissipation'],
      config.DENSITY_DISSIPATION,
    )
    this.blit(this.dye.write)
    this.dye.swap()
  }

  private splat(
    x: number,
    y: number,
    dx: number,
    dy: number,
    color: { r: number; g: number; b: number; a: number },
  ) {
    const gl = this.gl

    this.programs.splat.bind()
    gl.uniform1i(
      this.programs.splat.uniforms['uTarget'],
      this.velocity.read.attach(0),
    )
    gl.uniform1f(
      this.programs.splat.uniforms['aspectRatio'],
      this.canvas.width / this.canvas.height,
    )
    gl.uniform2f(this.programs.splat.uniforms['point'], x, y)
    gl.uniform4f(this.programs.splat.uniforms['color'], dx, dy, 0.0, 1.0)
    gl.uniform1f(
      this.programs.splat.uniforms['radius'],
      this.correctRadius(config.SPLAT_RADIUS / 100.0),
    )
    this.blit(this.velocity.write)
    this.velocity.swap()

    gl.uniform1i(
      this.programs.splat.uniforms['uTarget'],
      this.dye.read.attach(0),
    )
    gl.uniform4f(
      this.programs.splat.uniforms['color'],
      color.r,
      color.g,
      color.b,
      color.a,
    )
    this.blit(this.dye.write)
    this.dye.swap()
  }

  private correctRadius(radius: number): number {
    const aspectRatio = this.canvas.width / this.canvas.height
    if (aspectRatio > 1) {
      return radius * aspectRatio
    }
    return radius
  }

  private render() {
    const gl = this.gl

    gl.enable(gl.BLEND)
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA)

    this.programs.display.bind()
    gl.uniform1i(
      this.programs.display.uniforms['uTexture'],
      this.dye.read.attach(0),
    )
    this.blit(null, true)
  }

  public destroy() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId)
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect()
    }
    if (this.canvas && this.canvas.parentNode) {
      this.canvas.parentNode.removeChild(this.canvas)
    }
  }
}

// Initialize on DOM ready
export function initFooterShader(): void {
  const container = document.querySelector(
    '[data-footer-shader]',
  ) as HTMLElement

  if (container) {
    new FluidSimulation(container)
  }
}

// Auto-init
if (typeof window !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFooterShader)
  } else {
    initFooterShader()
  }
}

export default FluidSimulation
